import * as React from 'react';
import { useState } from 'react';
import PropTypes from 'prop-types';
import { theme } from '../../../tailwind.config';
import LoadingState from '../../styled components/LoadingState';
import WidgetPageSubHeader from '../../components/PageHeaders/WidgetPageSubHeader';
import EventDetailPageBody from './EventDetailPageBody';
import ContainerWrapper from '../../styled components/ContainerWrapper';
import Chip from '../../components/Chip/Chip';
import { resetWidgetTransformValue } from '../../libs/widgetUtilities';
import { getDefaultAppScrollContainer } from '../../libs/fixDefaultScrollposition';

import {
  HeaderTitleButton,
  HeaderTitleButtonContent,
  HeaderTitleExpandIcon,
  SubheaderAnimationContainer,
  SubHeaderIcon,
  SubHeaderItem,
  SubHeaderMainItemIconContainer
} from './EventDetailPage.style';
import { Meta } from '../../styled components/Typography';
import FormatSettingsContext from '../../components/FormatSettingsContext';
import YPEventObject from '../../yesplan_classes/YPEventObject';


const EventDetailPage = ({
  event = {},
  setCustomRendererForHeaderTitle,
  widgetPageHeaderRef,
  previousURL,
  allContentFetched,
  widgetAnimationWrapperRef,
  ...props
}) => {
  const [showClickableHeader, setShowClickableHeader] = useState(false);
  const [showEventInfoSummaryCopy, setShowEventInfoSummaryCopy] = useState(false);
  const [eventSummaryHeight, setEventSummaryHeight] =
    React.useState(
      1
    ); /* Don't use 0 as initial value, otherwise going from roster->event and resetting scroll to 0 triggers scroll listener here and collapsed the summary */
  const [pageHeaderHeight, setPageHeaderHeight] = React.useState(0);
  /* Needed to trigger a rerender when resizing the window */
  const [dimensions, setDimensions] = React.useState({
    height: window.innerHeight,
    width: window.innerWidth
  });

  const eventInfoSummaryRef = React.useCallback((node) => {
    if (node !== null) setEventSummaryHeight(node.clientHeight);
  }, []);

  const toggleEventInfoSummaryCopy = React.useCallback(() => {
    if (!showEventInfoSummaryCopy) {
      if (widgetAnimationWrapperRef && widgetAnimationWrapperRef.current) {
        resetWidgetTransformValue(widgetAnimationWrapperRef.current);
      }
    }
    setShowEventInfoSummaryCopy((show) => !show);
  }, [showEventInfoSummaryCopy, event.eventNameOrPlaceholder()]);

  /*
   *  Render functions that are passed to nested components
   */
  const renderClickableHeader = (collapsed) => (
    <HeaderTitleButton onClick={toggleEventInfoSummaryCopy}>
      <HeaderTitleButtonContent>
        {event.eventNameOrPlaceholder()}
      </HeaderTitleButtonContent>
      <HeaderTitleExpandIcon
        icon={collapsed ? 'expand-top-point' : 'expand-bottom-point'}
      />
    </HeaderTitleButton>
  );

  const renderSubHeaderMainContent = () => {
    const { dateFormatSetting, timeFormatSetting } =
      React.useContext(FormatSettingsContext);
    const items = [
      [false, event.eventNameOrPlaceholder()],
      ['clock', event.printDateAndTimeString(true, timeFormatSetting, dateFormatSetting)],
      ['map-marker', event.printLocationsString()]
    ];
    if (event.isPartOfGroup()) items.push(['tree-view', event.getNameOfGroup()]);
    return items.map((item) => (
      <SubHeaderItem key={`mainContent${item[0]}`} $marginBottom $responsiveMargin>
        {item[0] && (
          <SubHeaderMainItemIconContainer>
            <SubHeaderIcon size="small" icon={item[0]} />
          </SubHeaderMainItemIconContainer>
        )}
        <Meta>{item[1]}</Meta>
      </SubHeaderItem>
    ));
  };

  const renderStatusChip = (text) => {
    const { statuses } = props;
    const status = statuses[event.status.id];
    const bgColor = status && status.backgroundcolor ? status.backgroundcolor : '';
    const textColor = status && status.textcolor ? status.textcolor : '';
    return (
      <SubHeaderItem key={text} $marginRight>
        <Chip bgColor={bgColor} textColor={textColor}>
          {text}
        </Chip>
      </SubHeaderItem>
    );
  };

  const renderProfileChip = (text) => {

    const { profiles } = props;
    const profile = profiles[event.profile.id];
    const borderColor = profile && profile.color ? profile.color : '';
    return (
      <SubHeaderItem key={text} $marginRight>
        <Chip bgColor={theme.colors.white} borderColor={borderColor}>
          {text}
        </Chip>
      </SubHeaderItem>
    );
  };

  const renderSubHeaderBodyContent = () => {
    return (
      <>
        {renderStatusChip(event.printStatusString())}
        {renderProfileChip(event.printProfileString())}
      </>
    );
  };

  /*
   *  Effects on the header and subheader
   */
  React.useEffect(() => {
    if (showClickableHeader)
      setCustomRendererForHeaderTitle(() =>
        renderClickableHeader(showEventInfoSummaryCopy)
      );
  }, [showEventInfoSummaryCopy, showClickableHeader]);

  React.useEffect(() => {
    /* If the WidgetPageSubHeader component is scrolled out of view; show the page header title as a button
     * On click of the page header title button: toggle the WidgetPageSubHeader Copy component in to view
     * On scroll of the window always hide the WidgetPageSubHeader Copy component */
    const triggerRerender = () => {
      /* Trigger a rerender on a resize by setting a state property.
         The rerender will cause the callbacks on the pageHeaderRef and eventInfoSummaryRef to be called */
      setDimensions({
        height: window.innerHeight,
        width: window.innerWidth
      });
    };
    const scrollContainer = getDefaultAppScrollContainer();
    const determineRendererForHeaderTitle = () => {
      const yPos = scrollContainer ? scrollContainer.scrollTop : 0;
      const scrollMarker = yPos >= eventSummaryHeight;
      if (scrollMarker)
        setCustomRendererForHeaderTitle(() =>
          renderClickableHeader(showEventInfoSummaryCopy)
        );
      else setCustomRendererForHeaderTitle(() => event.eventNameOrPlaceholder());
    };
    const handleScroll = () => {
      const yPos = scrollContainer.scrollTop;
      const scrollMarker = yPos >= eventSummaryHeight;

      setShowClickableHeader(scrollMarker);
      determineRendererForHeaderTitle();
      /* Hide EventInfoSummaryCopy on scroll */
      setShowEventInfoSummaryCopy(false);
    };
    if (widgetPageHeaderRef && widgetPageHeaderRef.current)
      setPageHeaderHeight(widgetPageHeaderRef.current.getHeaderHeight());

    if (scrollContainer) scrollContainer.addEventListener('scroll', handleScroll, false);
    window.addEventListener('resize', triggerRerender);

    /* Make sure the correct name is set in the header when navigating directly to an event or switching tabs */
    determineRendererForHeaderTitle();

    /* Cleanup function */
    return () => {
      if (scrollContainer)
        scrollContainer.removeEventListener('scroll', handleScroll, false);
      window.removeEventListener('resize', triggerRerender);
    };
  }, [eventSummaryHeight, widgetPageHeaderRef]);

  /*
   *  Rendering of EventDetailPage
   */
  if (!allContentFetched)
    return (
      <div tw="relative mt-48 pt-48">
        <LoadingState $show={!allContentFetched} />
      </div>
    );
  const RenderedEventInfoSummary = (
    <WidgetPageSubHeader
      renderMainContent={renderSubHeaderMainContent}
      renderBodyContent={renderSubHeaderBodyContent}
      showClickableHeader={showClickableHeader}
      toggleEventInfoSummaryCopy={toggleEventInfoSummaryCopy}
      {...props}
    />
  );
  return (
    <>
      <ContainerWrapper $paddingTop={pageHeaderHeight} />
      {/* Key needs dependency on window height and window width to recompute sizes after a resize */}
      <div
        style={{ zIndex: '90' }}
        key={`EventInfoSummary${dimensions.height}${dimensions.width}`}
        id="eventInfoSummary"
        ref={eventInfoSummaryRef}
      >
        {RenderedEventInfoSummary}
      </div>
      {/* WidgetPageSubHeader Copy that can be toggled on click */}
      <SubheaderAnimationContainer
        key="EventInfoSummaryCopy"
        top={pageHeaderHeight - eventSummaryHeight}
        initial={false}
        animate={{ y: showEventInfoSummaryCopy ? `${eventSummaryHeight}px` : '0px' }}
        transition={{ ease: 'easeIn', duration: 0.3 }}
      >
        {RenderedEventInfoSummary}
      </SubheaderAnimationContainer>
      <EventDetailPageBody
        event={event}
        pageHeaderHeight={pageHeaderHeight}
        eventSummaryHeight={eventSummaryHeight}
        isHeaderShown={showClickableHeader}
        isCollapsibleHeaderShown={showEventInfoSummaryCopy}
        widgetAnimationWrapperRef={widgetAnimationWrapperRef}
        {...props}
      />
    </>
  );
};

EventDetailPage.propTypes = {
  event: PropTypes.instanceOf(YPEventObject),
  setCustomRendererForHeaderTitle: PropTypes.func.isRequired,
  widgetPageHeaderRef: PropTypes.shape({
    current: PropTypes.oneOfType([() => null, PropTypes.shape({})])
  }).isRequired,
  previousURL: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]).isRequired,
  statuses: PropTypes.shape({}).isRequired,
  profiles: PropTypes.shape({}).isRequired,
  allContentFetched: PropTypes.bool.isRequired,
  widgetAnimationWrapperRef: PropTypes.shape({
    current: PropTypes.oneOfType([() => null, PropTypes.shape({})])
  }).isRequired
};

export default EventDetailPage;
